import { useState, useRef, useEffect } from "react"
import { router } from "@inertiajs/react"
import confetti from "canvas-confetti"
import { track } from "@/lib/analytics"
import type { Video, Script, Render, SocialConnection } from "@/types"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import PublishDialog from "./publish-dialog"
import SocialPostsList from "./social-posts-list"
import CopyShareLinkButton from "./copy-share-link-button"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16 Portrait",
  landscape: "16:9 Landscape",
  square: "1:1 Square",
}

export type RenderProgressData = {
  step: "preparing" | "rendering" | "finalising"
  status: "in_progress" | "completed" | "failed"
  detail: string
  progress: number
  video_url?: string
}

export default function WizardStepRender({
  video,
  scripts,
  renders,
  connections,
  onRender,
  onGoToStoryboard,
  isRendering,
  renderProgress,
  renderError,
}: {
  video: Video
  scripts: Script[]
  renders: Render[]
  connections?: SocialConnection[]
  onRender: () => void
  onGoToStoryboard: () => void
  isRendering: boolean
  renderProgress?: RenderProgressData
  renderError?: string | null
}) {
  const activeScript = scripts.find((s) => s.active)
  const completedRenders = renders.filter((r) => r.status === "completed")
  const latestRender = completedRenders[0] ?? null
  const scenes = (activeScript?.content as { scenes?: Array<{ type?: string }> })?.scenes ?? []
  const sceneCount = scenes.filter((s) => s.type !== "closing").length

  // If rendering or just completed, show the progress/result view
  if (isRendering && renderProgress) {
    return (
      <RenderingView
        progress={renderProgress}
        onRetry={onRender}
        onGoToStoryboard={onGoToStoryboard}
      />
    )
  }

  // If we have a completed render, show the result
  if (latestRender) {
    return (
      <CompletedView
        video={video}
        latestRender={latestRender}
        previousRenders={completedRenders.slice(1)}
        connections={connections}
        activeScript={activeScript}
        onRenderAgain={onRender}
        onGoToStoryboard={onGoToStoryboard}
        isRendering={isRendering}
        renderError={renderError}
      />
    )
  }

  // Default: ready to render
  return (
    <ReadyView
      video={video}
      activeScript={activeScript}
      sceneCount={sceneCount}
      onRender={onRender}
      isRendering={isRendering}
      renderError={renderError}
    />
  )
}

// ---------------------------------------------------------------------------
// Ready to Render
// ---------------------------------------------------------------------------

function ReadyView({
  video,
  activeScript,
  sceneCount,
  onRender,
  isRendering,
  renderError,
}: {
  video: Video
  activeScript: Script | undefined
  sceneCount: number
  onRender: () => void
  isRendering: boolean
  renderError?: string | null
}) {
  const hasScript = !!activeScript
  const hasVoiceover = activeScript?.voiceover_status === "completed"
  const hasMusic = video.music_status === "completed"
  const enhancedCount = video.scene_assets.filter(
    (sa) => sa.asset_type === "enhanced_image" && sa.status === "completed",
  ).length
  const animatedCount = video.scene_assets.filter(
    (sa) => sa.asset_type === "animated_clip" && sa.status === "completed",
  ).length
  const hasStoryboard = enhancedCount > 0 || animatedCount > 0

  const checks = [
    { label: "Script", ready: hasScript, detail: hasScript ? `${sceneCount} scenes` : "Not generated" },
    { label: "Voiceover", ready: hasVoiceover, detail: hasVoiceover ? "Recorded" : "Not recorded", optional: true },
    { label: "Music", ready: hasMusic, detail: hasMusic ? "Generated" : "Not generated", optional: true },
    { label: "Storyboard", ready: hasStoryboard, detail: hasStoryboard ? `${enhancedCount} enhanced, ${animatedCount} animated` : "Original photos", optional: true },
  ]

  return (
    <div>
      <div className="mb-8 text-center">
        <h2 className="font-display text-2xl font-semibold text-charcoal">
          Render your video
        </h2>
        <p className="mt-1 text-warm-grey">
          Everything looks good. Hit render and we&rsquo;ll have your video ready in about two minutes.
        </p>
      </div>

      {/* Readiness + settings panels */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        {/* Readiness checklist */}
        <div className="rounded-xl border border-stone/30 bg-white p-5">
          <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Readiness
          </h3>
          <div className="space-y-3">
            {checks.map((check) => (
              <div key={check.label} className="flex items-center gap-3">
                <div className={`flex size-6 shrink-0 items-center justify-center rounded-full ${
                  check.ready
                    ? "bg-red text-white"
                    : "border border-stone/40 bg-stone/10 text-warm-grey/50"
                }`}>
                  {check.ready ? (
                    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  ) : (
                    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm font-medium ${check.ready ? "text-charcoal" : "text-warm-grey"}`}>
                      {check.label}
                    </span>
                    {check.optional && !check.ready && (
                      <span className="text-[10px] text-warm-grey/60">optional</span>
                    )}
                  </div>
                  <p className="text-[11px] text-warm-grey">{check.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Settings summary */}
        <div className="rounded-xl border border-stone/30 bg-white p-5">
          <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Settings
          </h3>
          <div className="space-y-3">
            <SettingRow label="Format" value={FORMAT_LABELS[video.aspect_ratio]} />
            <SettingRow label="Template" value={capitalise(video.template_variant)} />
            <SettingRow label="Transitions" value={capitalise(video.transition_style)} />
            <SettingRow label="Duration" value={`${video.target_length}s`} />
            <SettingRow label="Logo" value={video.show_logo ? "Shown" : "Hidden"} />
          </div>
        </div>
      </div>

      {/* Render CTA */}
      <div className="flex flex-col items-center">
        {renderError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red/20 bg-red/5 px-4 py-2.5">
            <svg className="size-4 shrink-0 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
            <p className="text-sm text-red">{renderError}</p>
          </div>
        )}

        <p className="mb-6 font-display text-xl font-medium text-charcoal">
          Ready to wow them?
        </p>

        <button
          type="button"
          onClick={onRender}
          disabled={isRendering || !activeScript}
          className="rounded-xl bg-red px-16 py-4 text-sm font-semibold text-white shadow-lg shadow-red/20 transition-all hover:bg-red/90 hover:shadow-xl hover:shadow-red/30 disabled:opacity-40 disabled:shadow-none"
        >
          {isRendering ? "Starting..." : "Render video"}
        </button>

        {!activeScript && (
          <p className="mt-3 text-xs text-red">Generate a script before rendering</p>
        )}
        {activeScript && (
          <p className="mt-3 text-[11px] text-warm-grey">
            Usually takes 1&ndash;2 minutes
          </p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Rendering in progress
// ---------------------------------------------------------------------------

const PIPELINE_STEPS = [
  { key: "preparing", label: "Preparing", icon: PackageIcon },
  { key: "rendering", label: "Rendering", icon: FilmIcon },
  { key: "finalising", label: "Finalising", icon: SparklesIcon },
] as const

export function RenderingView({
  progress,
  onRetry,
  onGoToStoryboard,
}: {
  progress: RenderProgressData
  onRetry: () => void
  onGoToStoryboard: () => void
}) {
  const currentIdx = PIPELINE_STEPS.findIndex((s) => s.key === progress.step)
  const isCompleted = progress.status === "completed"
  const isFailed = progress.status === "failed"
  const progressBarRef = useRef<HTMLDivElement>(null)

  // Animate progress bar smoothly
  useEffect(() => {
    if (progressBarRef.current) {
      const pct = isCompleted ? 100 : progress.progress
      progressBarRef.current.style.width = `${pct}%`
    }
  }, [progress.progress, isCompleted])

  // Confetti on completion
  useEffect(() => {
    if (!isCompleted) return
    const burst = (opts: confetti.Options) =>
      confetti({ ...opts, disableForReducedMotion: true })
    burst({ particleCount: 60, spread: 55, origin: { x: 0.3, y: 0.6 }, colors: ["#C41E2A", "#D4A853", "#1A1A1A", "#E8E4DF"] })
    burst({ particleCount: 60, spread: 55, origin: { x: 0.7, y: 0.6 }, colors: ["#C41E2A", "#D4A853", "#1A1A1A", "#E8E4DF"] })
    setTimeout(() => {
      burst({ particleCount: 40, spread: 70, origin: { x: 0.5, y: 0.5 }, colors: ["#C41E2A", "#D4A853", "#E8E4DF"] })
    }, 300)
  }, [isCompleted])

  return (
    <div>
      <div className="mb-10 text-center">
        <h2 className="font-display text-2xl font-semibold text-charcoal">
          {isCompleted ? "Your video is ready" : isFailed ? "Render failed" : "Rendering your video"}
        </h2>
        {!isCompleted && !isFailed && (
          <p className="mt-1 text-warm-grey">Sit tight — this usually takes a couple of minutes.</p>
        )}
      </div>

      {/* Pipeline visualisation */}
      <div className="mx-auto max-w-sm">
        <div className="relative flex justify-between">
          {/* Connector line */}
          <div className="pointer-events-none absolute left-[24px] right-[24px] top-6 h-px bg-stone/40" />
          <div
            className="pointer-events-none absolute left-[24px] top-6 h-px transition-all duration-700"
            style={{
              width: currentIdx === 0 ? "0%" : `${(currentIdx / (PIPELINE_STEPS.length - 1)) * 100}%`,
              maxWidth: "calc(100% - 48px)",
              background: isCompleted ? "#1A1A1A" : "#C41E2A",
            }}
          />

          {PIPELINE_STEPS.map((step, i) => {
            const isActive = i === currentIdx
            const isDone = i < currentIdx || (isActive && isCompleted)
            const isCurrent = isActive && progress.status === "in_progress"
            const Icon = step.icon

            return (
              <div key={step.key} className="relative z-10 flex flex-col items-center gap-2">
                <div className={`flex size-12 items-center justify-center rounded-full transition-all duration-500 ${
                  isDone
                    ? "bg-charcoal text-white"
                    : isCurrent
                      ? "bg-red text-white shadow-lg shadow-red/20"
                      : isActive && isFailed
                        ? "bg-red/10 text-red"
                        : "border border-stone/30 bg-white text-warm-grey/40"
                }`}>
                  {isDone ? (
                    <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  ) : isCurrent ? (
                    <div className="animate-pulse">
                      <Icon className="size-5" />
                    </div>
                  ) : isActive && isFailed ? (
                    <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <Icon className="size-5" />
                  )}
                </div>
                <span className={`text-xs font-medium ${
                  isActive ? "text-charcoal" : isDone ? "text-slate" : "text-warm-grey/50"
                }`}>
                  {step.label}
                </span>
              </div>
            )
          })}
        </div>

        {/* Progress bar */}
        {!isFailed && (
          <div className="mt-8 overflow-hidden rounded-full bg-stone/15">
            <div
              ref={progressBarRef}
              className={`h-2 rounded-full transition-all duration-700 ease-out ${
                isCompleted ? "bg-charcoal" : "bg-red"
              }`}
              style={{ width: "0%" }}
            />
          </div>
        )}

        {/* Detail + percentage */}
        <div className="mt-4 flex items-center gap-2">
          {progress.status === "in_progress" && (
            <svg className="size-3.5 animate-spin text-red" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {isCompleted && (
            <svg className="size-3.5 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
          )}
          <p className={`text-sm ${
            isFailed ? "font-medium text-red" : isCompleted ? "font-medium text-charcoal" : "text-warm-grey"
          }`}>
            {progress.detail}
          </p>
        </div>

        {/* Retry + back actions on failure */}
        {isFailed && (
          <div className="mt-8 flex flex-col items-center gap-3">
            <Button onClick={onRetry} className="bg-red px-8 font-semibold text-white shadow-sm hover:bg-deep-red">
              Try again
            </Button>
            <button
              type="button"
              onClick={onGoToStoryboard}
              className="text-sm text-warm-grey underline underline-offset-2 transition-colors hover:text-charcoal"
            >
              Back to storyboard
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Completed — video player + history
// ---------------------------------------------------------------------------

function CompletedView({
  video,
  latestRender,
  previousRenders,
  connections,
  activeScript,
  onRenderAgain,
  onGoToStoryboard,
  isRendering,
  renderError,
}: {
  video: Video
  latestRender: Render
  previousRenders: Render[]
  connections?: SocialConnection[]
  activeScript?: Script
  onRenderAgain: () => void
  onGoToStoryboard: () => void
  isRendering: boolean
  renderError?: string | null
}) {
  const videoSrc = latestRender.video_url || latestRender.output_url
  const aspectClasses: Record<Video["aspect_ratio"], string> = {
    portrait: "mx-auto max-w-xs aspect-[9/16]",
    landscape: "w-full aspect-video",
    square: "mx-auto max-w-md aspect-square",
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="font-display text-2xl font-semibold text-charcoal">
          Your video
        </h2>
        <p className="mt-1 text-warm-grey">
          Looking good. Download it, or render again with different settings.
        </p>
      </div>

      {/* Video player */}
      {videoSrc && (
        <div className={`${aspectClasses[video.aspect_ratio]} mb-6 overflow-hidden rounded-xl bg-charcoal shadow-xl shadow-charcoal/15`}>
          <video
            src={videoSrc}
            controls
            className="size-full object-contain"
            preload="metadata"
          />
        </div>
      )}

      {/* Render meta + actions */}
      <div className="mb-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <Badge variant="outline" className="text-[10px] capitalize">
            {latestRender.template_variant}
          </Badge>
          <Badge variant="outline" className="text-[10px] capitalize">
            {latestRender.transition_style}
          </Badge>
          {latestRender.duration_ms && (
            <Badge variant="outline" className="text-[10px]">
              {Math.round(latestRender.duration_ms / 1000)}s
            </Badge>
          )}
          <span className="text-[11px] text-warm-grey">
            {new Date(latestRender.created_at).toLocaleDateString("en-GB", {
              day: "numeric", month: "short", year: "numeric",
            })}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-2">
        {connections && connections.length > 0 && (
          <PublishDialog
            videoId={video.id}
            render={latestRender}
            connections={connections}
            activeScript={activeScript}
            onPublished={() => router.reload()}
            size="default"
            variant="red"
            label="Post"
          />
        )}

        <Button
          onClick={onRenderAgain}
          disabled={isRendering}
          className="bg-red text-white hover:bg-deep-red"
        >
          <svg className="mr-1.5 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
          </svg>
          {isRendering ? "Starting..." : "Render again"}
        </Button>

        {/* Secondary icon actions */}
        <div className="flex items-center gap-1.5">
          <TooltipProvider>
            {videoSrc && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild variant="secondary" size="icon">
                    <a href={videoSrc} download onClick={() => track("video_downloaded", { render_id: latestRender.id })}>
                      <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Download MP4</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <CopyShareLinkButton videoId={video.id} size="icon" iconOnly />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">Copy share link</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {renderError && (
        <div className="mt-3 text-center">
          <p className="text-sm text-red">{renderError}</p>
        </div>
      )}

      {/* Back to storyboard link */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onGoToStoryboard}
          className="text-sm text-warm-grey underline underline-offset-2 transition-colors hover:text-charcoal"
        >
          Back to storyboard
        </button>
      </div>

      {/* Social posts */}
      {latestRender.social_posts && latestRender.social_posts.length > 0 && (
        <SocialPostsList
          posts={latestRender.social_posts}
          videoId={video.id}
          renderId={latestRender.id}
          onPostRemoved={() => router.reload()}
        />
      )}

      {/* Previous renders */}
      {previousRenders.length > 0 && (
        <PreviousRendersList videoId={video.id} renders={previousRenders} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function PreviousRendersList({ videoId, renders }: { videoId: string; renders: Render[] }) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  function handleDelete(renderId: string) {
    setDeletingId(renderId)
    router.delete(`/dashboard/videos/${videoId}/renders/${renderId}`, {
      preserveScroll: true,
      onFinish: () => setDeletingId(null),
    })
  }

  return (
    <div className="mt-10 border-t border-stone/20 pt-6">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
        Previous renders
      </h3>
      <div className="space-y-2">
        {renders.map((r) => (
          <div
            key={r.id}
            className="group flex items-center justify-between rounded-lg border border-stone/15 bg-cream/20 px-4 py-2.5"
          >
            <div className="flex items-center gap-2.5">
              <RenderStatusDot status={r.status} />
              <span className="text-xs text-warm-grey capitalize">
                {r.template_variant} &middot; {r.transition_style}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-warm-grey/60">
                {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    disabled={deletingId === r.id}
                    className="rounded p-0.5 text-warm-grey/40 opacity-0 transition-all hover:text-red group-hover:opacity-100 disabled:opacity-50"
                    aria-label="Delete render"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent size="sm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete render?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this render. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={() => handleDelete(r.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-warm-grey">{label}</span>
      <span className="text-sm font-medium text-charcoal">{value}</span>
    </div>
  )
}

function RenderStatusDot({ status }: { status: Render["status"] }) {
  const colours: Record<Render["status"], string> = {
    pending: "bg-stone/40",
    rendering: "bg-amber-400",
    completed: "bg-charcoal",
    failed: "bg-red",
    timed_out: "bg-red",
  }
  return <span className={`inline-block size-2 rounded-full ${colours[status]}`} />
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function PackageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  )
}

function FilmIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0 1 18 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0 1 18 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 0 1 6 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h1.5m14.25 0h1.5" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
    </svg>
  )
}
